import {
  IAutomationActionTemplateMicrosoftInviteToChannel,
  IAutomationTemplate,
  MicrosoftTeamsTeam
} from '@dadohr/shared/domainTypes/experience';
import { MenuItem } from '@material-ui/core';
import { sortBy } from 'lodash';
import { Lock } from 'lucide-react';
import { SetStateAction } from 'react';
import { AlertBox } from '../../../../../../../components/AlertBox';
import { AutocompleteSingle } from '../../../../../../../components/Autocomplete';
import { DataFieldWithLabel } from '../../../../../../../components/DataFieldWithLabel';
import { FormRow } from '../../../../../../../components/Form/FormRow';
import { InfoBox } from '../../../../../../../components/InfoBox';
import { TokenDefinition } from '../../../../../../../components/MarkdownText';
import { SelectCustom } from '../../../../../../../components/SelectCustom';
import { TextFieldWithReplacementTokens } from '../../../../../../../components/TextFieldWithReplacementTokens';
import { useUnsafeTranslation } from '../../../../../../../i18next';
import { FlexContainer } from '../../../../../../../layout/Flex';
import { joinWithCommaAndAnd } from '../../../../../../../services/enumerate';
import {
  MicrosoftTeamsChannel,
  getMicrosoftTeamsChannelList,
  getMicrosoftTeamsTeamList
} from '../../../../../../Org/pages/Integrations/integrations/microsoft/service';
import { TaskBuilderContext } from '../../../TaskBuilder/types';
import { ActionActorSelectorMulti, actionActorToString } from '../../shared';
import {
  AutomationBuilderActionValidationState,
  AutomationBuilderActionValidationStateItem,
  VALIDATORS_MICROSOFT_TEAMS_INVITE_TO_CHANNEL,
  useFieldValidator
} from '../../validation';

export const TeamSelector = ({
  orgId,
  value,
  onChange
}: {
  orgId: string;
  value: MicrosoftTeamsTeam | null;
  onChange: (nextTeam: MicrosoftTeamsTeam) => void;
}) => {
  const { t } = useUnsafeTranslation();
  return (
    <AutocompleteSingle
      label={t('Team')}
      fullWidth
      value={value}
      onChange={(nextValue) => {
        if (nextValue) {
          onChange(nextValue);
        }
      }}
      disabledClearable={true}
      options={() =>
        getMicrosoftTeamsTeamList(orgId).then((cs) =>
          sortBy(cs, (c) => c.name || 'ZZZZZZZZ')
        )
      }
      getOptionLabel={(o) => o.name}
    />
  );
};

const ChannelTypeSelector = ({
  value,
  disabled,
  onChange
}: {
  value: IAutomationActionTemplateMicrosoftInviteToChannel['channel']['type'];
  disabled: boolean;
  onChange: (
    nextValue: IAutomationActionTemplateMicrosoftInviteToChannel['channel']['type']
  ) => void;
}) => {
  const { t } = useUnsafeTranslation();
  return (
    <SelectCustom
      disabled={disabled}
      fullWidth
      label={t('New or Existing Channel')}
      value={value}
      onChange={(ev) => {
        onChange(
          ev.target
            .value as IAutomationActionTemplateMicrosoftInviteToChannel['channel']['type']
        );
      }}
    >
      <MenuItem value="EXISTING">{t('Use existing channel')}</MenuItem>
      <MenuItem value="NEW">{t('Create a new channel')}</MenuItem>
    </SelectCustom>
  );
};

export const ExistingChannelSelector = ({
  orgId,
  team,
  value,
  onChange
}: {
  orgId: string;
  team: MicrosoftTeamsTeam;
  value: MicrosoftTeamsChannel;
  onChange: (nextChannel: MicrosoftTeamsChannel) => void;
}) => {
  const { t } = useUnsafeTranslation();
  return (
    <AutocompleteSingle
      label={t('Channel')}
      disabled={!team.id}
      fullWidth
      value={value}
      onChange={(nextValue) => {
        if (nextValue) {
          onChange(nextValue);
        }
      }}
      disabledClearable={true}
      options={() =>
        team.id
          ? getMicrosoftTeamsChannelList(orgId, team.id).then((cs) =>
              sortBy(cs, (c) => c.name || 'ZZZZZZZZ').filter(
                (a, b) => a.kind !== 'STANDARD'
              )
            )
          : Promise.resolve([])
      }
      getOptionLabel={(o) => o.name}
      renderOption={(o) => (
        <FlexContainer>
          {o.kind === 'PRIVATE' && <Lock size={16} />}
          <div>{o.name}</div>
        </FlexContainer>
      )}
    />
  );
};

export const ActionSettingsMicrosoftTeamsInviteToChannel = ({
  value,
  onChange,
  expTpl,
  validation,
  aTpl,
  setValidation,
  tokens
}: {
  value: IAutomationActionTemplateMicrosoftInviteToChannel;
  onChange: (
    nextValue: IAutomationActionTemplateMicrosoftInviteToChannel
  ) => void;
  expTpl: TaskBuilderContext;
  aTpl: IAutomationTemplate;
  validation: AutomationBuilderActionValidationStateItem | null;
  setValidation: React.Dispatch<
    SetStateAction<AutomationBuilderActionValidationState>
  >;
  tokens: TokenDefinition[];
}) => {
  const { t } = useUnsafeTranslation();

  const validatePplToAdd = useFieldValidator(
    setValidation,
    VALIDATORS_MICROSOFT_TEAMS_INVITE_TO_CHANNEL,
    'pplToAdd'
  );
  const validateChannelType = useFieldValidator(
    setValidation,
    VALIDATORS_MICROSOFT_TEAMS_INVITE_TO_CHANNEL,
    'channelType'
  );
  const invalidChannelType = validation?.fields.channelType || null;
  const invalidPplToAdd = validation?.fields.pplToAdd || null;

  return (
    <div>
      <FormRow>
        <ActionActorSelectorMulti
          label={t('Who should be invited')}
          value={value.pplToAdd}
          onChange={(nextPplToAdd) => {
            const nextValue = { ...value, pplToAdd: nextPplToAdd };
            invalidPplToAdd && validatePplToAdd(nextValue, expTpl, aTpl);
            return onChange(nextValue);
          }}
          expTpl={expTpl}
          fullWidth
          error={!!invalidPplToAdd}
          helperText={invalidPplToAdd}
          onBlur={() => validatePplToAdd(value, expTpl, aTpl)}
        />
      </FormRow>
      <FormRow>
        <TeamSelector
          orgId={expTpl.orgId}
          value={value.team}
          onChange={(nextTeam) =>
            onChange({
              ...value,
              team: nextTeam
            })
          }
        />
      </FormRow>
      <FormRow>
        <ChannelTypeSelector
          disabled={!value.team.id}
          value={value.channel.type}
          onChange={(nextType) => {
            if (nextType === 'EXISTING') {
              onChange({
                ...value,
                channel: {
                  type: 'EXISTING',
                  kind: 'PRIVATE',
                  id: '',
                  name: ''
                }
              });
            }
            if (nextType === 'NEW') {
              onChange({
                ...value,
                channel: {
                  type: 'NEW',
                  kind: 'PRIVATE',
                  nameSchema: ''
                }
              });
            }
          }}
        />
      </FormRow>

      {value.channel.type === 'EXISTING' && (
        <>
          <FormRow>
            <ExistingChannelSelector
              team={value.team}
              orgId={expTpl.orgId}
              value={{
                id: value.channel.id,
                name: value.channel.name,
                kind: value.channel.kind
              }}
              onChange={(nextChannel) =>
                onChange({
                  ...value,
                  channel: {
                    ...value.channel,
                    ...nextChannel,
                    type: 'EXISTING'
                  }
                })
              }
            />
          </FormRow>
          <FormRow>
            <InfoBox color="blue">
              {t(
                'This automation can only add users to Private or Shared channels. To give a user access to standard channels, use the ‘Add to group/team’ automation to add them to the relevant team.'
              )}
            </InfoBox>
          </FormRow>
        </>
      )}
      {value.channel.type === 'NEW' && (
        <>
          <FormRow>
            <SelectCustom
              fullWidth
              error={!!invalidChannelType}
              helperText={invalidChannelType}
              label={t('Channel type')}
              value={value.channel.kind}
              onBlur={() => validateChannelType(value, expTpl, aTpl)}
              onChange={(ev) => {
                const nextValue = {
                  ...value,
                  channel: {
                    ...value.channel,
                    kind: ev.target
                      .value as IAutomationActionTemplateMicrosoftInviteToChannel['channel']['kind']
                  }
                };

                invalidChannelType &&
                  validateChannelType(nextValue, expTpl, aTpl);
                onChange(nextValue);
              }}
            >
              <MenuItem value="STANDARD">{t('Standard')}</MenuItem>
              <MenuItem value="PRIVATE">{t('Private')}</MenuItem>
              <MenuItem value="SHARED">{t('Shared')}</MenuItem>
            </SelectCustom>
          </FormRow>
          {value.channel.kind !== 'STANDARD' ? (
            <FormRow>
              <TextFieldWithReplacementTokens
                label="New Channel Name"
                helperText="Enter a channel name"
                fullWidth
                variant="outlined"
                value={value.channel.nameSchema}
                onChange={(ev) => {
                  value.channel.type === 'NEW' &&
                    onChange({
                      ...value,
                      channel: {
                        ...value.channel,
                        nameSchema: ev
                      }
                    });
                }}
                tokens={tokens}
              />
            </FormRow>
          ) : (
            <AlertBox color="blue">
              {t(
                'Add a user to a Team using the ‘Add to Group’ automation to give them access to all that Team’s standard channels.'
              )}
            </AlertBox>
          )}
        </>
      )}
    </div>
  );
};

export const ActionSettingsMicrosoftTeamsInviteToChannelReadOnly = ({
  value,
  expTpl
}: {
  value: IAutomationActionTemplateMicrosoftInviteToChannel;
  expTpl: TaskBuilderContext;
}) => {
  const { t } = useUnsafeTranslation();
  return (
    <div>
      <FormRow>
        <DataFieldWithLabel label={t('Who should be invited')}>
          {joinWithCommaAndAnd(
            value.pplToAdd.map((p) =>
              actionActorToString(p, expTpl.supportingActors, t)
            ),
            t
          )}
        </DataFieldWithLabel>
      </FormRow>

      <FormRow>
        <DataFieldWithLabel label={t('New or Existing Channel')}>
          {value.channel.type === 'NEW' && t('Create a new channel')}
          {value.channel.type === 'EXISTING' && t('Use existing channel')}
        </DataFieldWithLabel>
      </FormRow>

      {value.channel.type === 'NEW' && (
        <>
          <FormRow>
            <DataFieldWithLabel label={t('Channel type')}>
              {value.channel.kind === 'STANDARD' && t('Standard')}
              {value.channel.kind === 'PRIVATE' && t('Private')}
              {value.channel.kind === 'SHARED' && t('Shared')}
            </DataFieldWithLabel>
          </FormRow>

          {value.channel.kind !== 'STANDARD' && (
            <FormRow>
              <DataFieldWithLabel label={t('New Channel Name')}>
                {value.channel.nameSchema} {}
              </DataFieldWithLabel>
            </FormRow>
          )}
        </>
      )}

      {value.channel.type === 'EXISTING' && (
        <FormRow>
          <DataFieldWithLabel label={t('Channel')}>
            {value.channel.name}
          </DataFieldWithLabel>
        </FormRow>
      )}
    </div>
  );
};
